import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileText, Upload, Info } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const SUGGESTED_DOC_KEYS = [
  "application.suggestedDocIdentity",
  "application.suggestedDocSocialSecurity",
  "application.suggestedDocVigilance",
  "application.suggestedDocKbis",
];

/** Shared document exchange list for a single application, usable by both the producer and the
 * candidate side once an application is unlocked. `showSuggestions` (producer view only) displays
 * a reference checklist of documents typically requested from a seasonal worker. */
export default function ApplicationDocuments({ applicationId, documents, onUploaded, showSuggestions = false }) {
  const { t } = useTranslation();

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post(`/applications/${applicationId}/documents`, formData);
      onUploaded(data.shared_documents);
      toast.success(t("common.success"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, t));
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div data-testid={`application-documents-${applicationId}`}>
      {showSuggestions && (
        <div className="flex gap-2.5 bg-[#4ba3b7]/5 border border-[#4ba3b7]/20 rounded-xl p-3.5 mb-4" data-testid={`application-suggested-documents-${applicationId}`}>
          <Info className="h-4 w-4 text-[#4ba3b7] shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-[#2B2B2B] mb-1.5">{t("application.suggestedDocumentsTitle")}</p>
            <ul className="text-xs text-[#2B2B2B]/70 space-y-1 list-disc pl-4">
              {SUGGESTED_DOC_KEYS.map((key) => <li key={key}>{t(key)}</li>)}
            </ul>
          </div>
        </div>
      )}
      <p className="text-xs font-bold uppercase tracking-wide text-[#2B2B2B]/50 mb-2">{t("application.sharedDocumentsTitle")}</p>
      {(!documents || documents.length === 0) ? (
        <p className="text-sm text-[#2B2B2B]/40 mb-3" data-testid={`application-documents-empty-${applicationId}`}>{t("application.noDocumentsYet")}</p>
      ) : (
        <div className="space-y-1.5 mb-3">
          {documents.map((doc, idx) => (
            <a key={idx} href={`${API}/profiles/files/${doc.storage_path}`} target="_blank" rel="noreferrer"
               className="flex items-center gap-2 text-sm text-[#4ba3b7] hover:underline"
               data-testid={`application-document-link-${applicationId}-${idx}`}>
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate">{doc.filename}</span>
              <span className="text-xs text-[#2B2B2B]/40 shrink-0">
                ({doc.uploaded_by === "producer" ? t("application.uploadedByProducer") : t("application.uploadedByCandidate")})
              </span>
            </a>
          ))}
        </div>
      )}
      <label className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold text-[#4ba3b7] hover:underline">
        <Upload className="h-4 w-4" /> {t("application.uploadDocument")}
        <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={handleUpload}
               data-testid={`application-document-upload-input-${applicationId}`} />
      </label>
    </div>
  );
}
